import {
  Center,
  ChakraProps,
  HStack,
  Progress,
  Stack,
  Text,
  TextProps,
  useColorModeValue,
} from '@chakra-ui/react';
import React from 'react';

import {
  date as commonDate,
  finleyAssertUnreachable,
  utils as commonUtils,
} from '@finley-technologies/common';

import {
  DeliverableArtifactGQLType,
  DeliverableArtifactInstanceGQLType,
  DeliverableContainerGQLType,
  DeliverableContainerOutputLenderViewGQLType,
  DeliverableContainerReceiptActionTypeGQLType,
  Maybe,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';

export type DetailedStatus =
  | 'notStarted'
  | 'overdue'
  | 'finishUploading' // dueSoon (finish uploading / inputs incomplete)
  | 'readyToGenerateReport' // inputsComplete (need to generate the report)
  | 'readyToSubmit' // reportGenerated (report has been generate, but not shared with lender)
  // these are all report statuses as well
  | 'submitted'
  | 'requestRemediation'
  | 'approved';

export const detailedStatusColor: { [key in DetailedStatus]: string } = {
  overdue: 'red',
  finishUploading: 'orange',
  notStarted: 'blue',

  // required files done
  readyToGenerateReport: 'orange',

  // receipts exist
  readyToSubmit: 'orange',
  submitted: 'blue',
  requestRemediation: 'orange',
  approved: 'green',
};

export const detailedStatusIcon: { [key in DetailedStatus]: string } = {
  overdue: 'calendar-clock',
  finishUploading: 'file-arrow-up',
  notStarted: 'calendar-clock',

  // required files done
  readyToGenerateReport: 'files',

  // receipts exist
  readyToSubmit: 'mailbox-flag-up',
  submitted: 'mailbox',
  requestRemediation: 'messages-question',
  approved: 'circle-check',
};

export const DetailedStatusIcon = ({ status }: { status: DetailedStatus }) => {
  const colorScheme = detailedStatusColor[status];

  const color = useColorModeValue(`${colorScheme}.800`, `${colorScheme}.100`);
  return <Icon icon={detailedStatusIcon[status]} color={color} />;
};

export const DetailedStatusBadge = ({ status }: { status: DetailedStatus }) => {
  const colorScheme = detailedStatusColor[status];
  const bgColor = useColorModeValue(`${colorScheme}.50`, `${colorScheme}.800`);
  const color = useColorModeValue(`${colorScheme}.800`, `${colorScheme}.100`);
  return (
    <HStack
      padding="12px"
      borderRadius="8px"
      bgColor={bgColor}
      width="fit-content"
    >
      <DetailedStatusIcon status={status} />
      <Text textAlign="center" fontWeight="semibold" color={color}>
        {commonUtils.toTitleCase(status)}
      </Text>
    </HStack>
  );
};

export const DetailedStatusCell = ({
  status,
  ...textProps
}: {
  status: DetailedStatus;
} & TextProps) => {
  return (
    <HStack alignItems="center">
      <Center paddingRight="5px">
        <DetailedStatusIcon status={status} />
      </Center>
      <Text {...textProps}>{commonUtils.toTitleCase(status)}</Text>
    </HStack>
  );
};

export const calculateDetailedStatus = (
  container: DeliverableContainerGQLType,
): DetailedStatus => {
  const currentDate = commonDate.currentDateStr();
  const diff = commonDate.diffDays(container.dueDate, currentDate);

  if (container.mostRecentActionType) {
    switch (container.mostRecentActionType) {
      case 'approved':
        return 'approved';
      case 'requestRemediation':
        return 'requestRemediation';
      case 'submitted':
        return 'submitted';
      case 'generated':
        return 'readyToSubmit';
      default:
        finleyAssertUnreachable(container.mostRecentActionType);
    }
  }

  if (
    container.totalFinalArtifacts === -1 &&
    container.totalDependencies === -1
  ) {
    // even if the deliverable has not been started, we still want to mark it as overdue
    if (diff < 0) {
      return 'overdue';
    }
    return 'notStarted';
  }
  if (container.completedDependencies === container.totalDependencies) {
    return 'readyToGenerateReport';
  }
  if (diff < 0) {
    return 'overdue';
  }
  return 'finishUploading';
};

// 'readyToSubmit' and 'readyToGenerateReport' are folded into the 'dueSoon' status
export type Status =
  | Exclude<
      DetailedStatus,
      'readyToGenerateReport' | 'readyToSubmit' | 'finishUploading'
    >
  | 'dueSoon';

export const statusColor: { [key in Status]: string } = {
  overdue: 'red',
  dueSoon: 'orange',
  notStarted: 'blue',

  // receipts exist
  submitted: 'blue',
  requestRemediation: 'orange',
  approved: 'green',
};

export const statusIcon: { [key in Status]: string } = {
  overdue: 'calendar-clock',
  dueSoon: 'calendar-clock',
  notStarted: 'calendar-clock',

  // receipts exist
  submitted: 'mailbox',
  requestRemediation: 'messages-question',
  approved: 'circle-check',
};

export const StatusIcon = ({ status }: { status: Status }) => {
  return (
    <Icon icon={statusIcon[status]} color={`${statusColor[status]}.300`} />
  );
};

export const StatusCell = ({ status }: { status: Status }) => {
  return (
    <HStack alignItems="center">
      <Center paddingRight="5px">
        <StatusIcon status={status} />
      </Center>
      <Text>{commonUtils.toTitleCase(status)}</Text>
    </HStack>
  );
};

export const calculateStatus = (
  container: DeliverableContainerGQLType,
): Status => {
  const currentDate = commonDate.currentDateStr();
  const diff = commonDate.diffDays(container.dueDate, currentDate);

  if (container.mostRecentActionType) {
    switch (container.mostRecentActionType) {
      case 'approved':
        return 'approved';
      case 'requestRemediation':
        return 'requestRemediation';
      case 'submitted':
        return 'submitted';
      case 'generated': {
        if (diff < 0) {
          return 'overdue';
        }
        return 'dueSoon';
      }
      default:
        finleyAssertUnreachable(container.mostRecentActionType);
    }
  }

  if (diff < 0) {
    // even if the deliverable has not been started, we still want to mark it as overdue
    // that's why this check comes before the not started/due soon distinction
    return 'overdue';
  }

  if (
    container.totalFinalArtifacts === -1 &&
    container.totalDependencies === -1
  ) {
    return 'notStarted';
  }
  return 'dueSoon';
};

export type ArtifactInstanceStatus =
  | 'submitted'
  | 'processing'
  | 'notYetSubmitted'
  | 'expiredVersion';

export type ArtifactStatus = 'submitted' | 'processing' | 'notYetSubmitted';

export const calculateArtifactInstanceStatus = (
  artifactInstance: Maybe<DeliverableArtifactInstanceGQLType>,
): ArtifactInstanceStatus => {
  if (artifactInstance && !artifactInstance.active) {
    return 'expiredVersion';
  }
  if (artifactInstance && artifactInstance.active) {
    if (artifactInstance.status === 'success') {
      return 'submitted';
    }
    if (artifactInstance.status === 'inProgress') {
      return 'processing';
    }
    // if artifactInstance.status === 'error' then artifact status should still be 'notYetSubmitted'
  }

  return 'notYetSubmitted';
};

export const calculateArtifactStatus = (
  artifact: DeliverableArtifactGQLType,
): ArtifactStatus => {
  const { instances } = artifact;

  const activeInstance = instances.find((i) => i && i.active);

  const artifactInstanceStatus = calculateArtifactInstanceStatus(
    activeInstance,
  );

  return artifactInstanceStatus === 'expiredVersion'
    ? 'notYetSubmitted'
    : artifactInstanceStatus;
};

export const artifactStatusColor: { [key in ArtifactStatus]: string } = {
  notYetSubmitted: 'orange',
  processing: 'orange',
  submitted: 'green',
};

export const artifactStatusIcon: { [key in ArtifactStatus]: string } = {
  notYetSubmitted: 'mailbox',
  processing: 'spinner-third',
  submitted: 'mailbox-flag-up',
};

export const UploadProgressBar = ({
  progressValue,
}: {
  progressValue: number;
}) => {
  return (
    <Stack width="max-content">
      <Text fontSize="sm" fontWeight="semibold">
        Upload in progress.
      </Text>
      <Progress
        value={progressValue}
        colorScheme="green"
        borderRadius={8}
        size="sm"
      />
    </Stack>
  );
};

export const UploadErrorCell = ({
  isTextColored = false,
}: {
  isTextColored: boolean;
}) => {
  return (
    <HStack alignItems="center" spacing="8px">
      <Icon icon="circle-x" color={colors.red[300]} />
      <Text
        color={isTextColored ? colors.red[300] : 'inherit'}
        fontWeight="semibold"
      >
        Upload Error
      </Text>
    </HStack>
  );
};

export const UploadErrorBadge = () => {
  return (
    <HStack
      spacing="8px"
      alignItems="center"
      borderRadius="8px"
      p="8px 16px"
      bgColor={colors.red[100]}
    >
      <Icon icon="circle-x" color={colors.red[300]} />
      <Text color={colors.red[300]} fontWeight="semibold">
        Upload Error
      </Text>
    </HStack>
  );
};

export const ArtifactStatusCell = ({
  status,
  isTextColored = false,
}: {
  status: ArtifactStatus;
  isTextColored: boolean;
}) => {
  const color = `${artifactStatusColor[status]}.300`;
  return (
    <HStack alignItems="center" spacing="8px">
      <Icon icon={artifactStatusIcon[status]} color={color} />
      <Text color={isTextColored ? color : 'inherit'}>
        {commonUtils.toSentenceCase(status)}
      </Text>
    </HStack>
  );
};

export const StatusBadge = ({
  icon,
  bgColor,
  color,
  status,
}: {
  icon: string;
  bgColor: ChakraProps['bgColor'];
  color: ChakraProps['color'];
  status: string;
}) => {
  return (
    <HStack
      spacing="8px"
      alignItems="center"
      borderRadius="8px"
      p="8px 16px"
      bgColor={bgColor}
    >
      <Icon icon={icon} color={color} />
      <Text color={color} fontWeight="semibold">
        {commonUtils.toTitleCase(status)}
      </Text>
    </HStack>
  );
};

export const ArtifactStatusBadge = ({ status }: { status: ArtifactStatus }) => {
  const colorScheme = artifactStatusColor[status];
  const bgColor = useColorModeValue(`${colorScheme}.50`, `${colorScheme}.800`);
  const color = useColorModeValue(`${colorScheme}.800`, `${colorScheme}.100`);

  return (
    <StatusBadge
      icon={artifactStatusIcon[status]}
      bgColor={bgColor}
      color={color}
      status={status}
    />
  );
};

export const ArtifactInstanceStatusBadge = ({
  status,
}: {
  status: ArtifactInstanceStatus;
}) => {
  switch (status) {
    case 'notYetSubmitted':
    case 'submitted':
      return <ArtifactStatusBadge status={status} />;
    case 'processing':
      return <ArtifactStatusBadge status="notYetSubmitted" />;
    case 'expiredVersion':
      return (
        <StatusBadge
          icon="calendar-circle-exclamation"
          bgColor="gray.50"
          color="black.70"
          status="expiredVersion"
        />
      );
    default:
      return finleyAssertUnreachable(status);
  }
};

export type ReceiptStatus =
  | 'readyToSubmit'
  | 'submitted'
  | 'approved'
  | 'remediationRequired';

export const calculateReceiptStatus = (
  action: DeliverableContainerReceiptActionTypeGQLType,
): ReceiptStatus => {
  switch (action) {
    case 'generated':
      return 'readyToSubmit';
    case 'submitted':
      return 'submitted';
    case 'approved':
      return 'approved';
    case 'requestRemediation':
      return 'remediationRequired';
    default:
      return finleyAssertUnreachable(action);
  }
};

export const receiptStatusColor: { [key in ReceiptStatus]: string } = {
  readyToSubmit: 'blue',
  submitted: 'blue',
  approved: 'green',
  remediationRequired: 'red',
};

export const receiptStatusIcon: { [key in ReceiptStatus]: string } = {
  readyToSubmit: 'circle-check',
  submitted: 'mailbox-flag-up',
  approved: 'circle-check',
  remediationRequired: 'messages-question',
};

export const ReceiptStatusBadge = ({
  mostRecentAction,
}: {
  mostRecentAction: DeliverableContainerReceiptActionTypeGQLType;
}) => {
  const status = calculateReceiptStatus(mostRecentAction);
  const colorScheme = receiptStatusColor[status];
  const bgColor = useColorModeValue(`${colorScheme}.50`, `${colorScheme}.800`);
  const color = useColorModeValue(`${colorScheme}.800`, `${colorScheme}.100`);

  const icon = receiptStatusIcon[status];

  return (
    <HStack
      alignItems="center"
      px="14px"
      py="10px"
      borderRadius="8px"
      bgColor={bgColor}
      width="fit-content"
    >
      <Center paddingRight="5px">
        <Icon icon={icon} color={color} />
      </Center>
      <Text
        color={color}
        fontSize="sm"
        fontWeight="semibold"
        textAlign="center"
      >
        {commonUtils.toTitleCase(status)}
      </Text>
    </HStack>
  );
};

// _____________________________________ LENDER STATUSES _____________________________________

export type LenderReceiptStatus =
  | 'readyForReview'
  | 'approved'
  | 'remediationRequested';

export const calculateLenderReceiptStatus = (
  action: DeliverableContainerReceiptActionTypeGQLType,
): LenderReceiptStatus => {
  switch (action) {
    case 'generated':
      throw new Error(
        'Lenders should never see a receipt with most recent status "generated".',
      );
    case 'submitted':
      return 'readyForReview';
    case 'approved':
      return 'approved';
    case 'requestRemediation':
      return 'remediationRequested';
    default:
      return finleyAssertUnreachable(action);
  }
};

export const lenderReceiptStatusColor: {
  [key in LenderReceiptStatus]: string;
} = {
  readyForReview: 'orange',
  approved: 'green',
  remediationRequested: 'orange',
};

export const lenderReceiptStatusIcon: {
  [key in LenderReceiptStatus]: string;
} = {
  readyForReview: 'mailbox-flag-up',
  approved: 'circle-check',
  remediationRequested: 'messages-question',
};

export const LenderReceiptStatusBadge = ({
  mostRecentAction,
}: {
  mostRecentAction: DeliverableContainerReceiptActionTypeGQLType;
}) => {
  const status = calculateLenderReceiptStatus(mostRecentAction);
  const colorScheme = lenderReceiptStatusColor[status];
  const bgColor = useColorModeValue(`${colorScheme}.50`, `${colorScheme}.800`);
  const color = useColorModeValue(`${colorScheme}.800`, `${colorScheme}.100`);

  const icon = lenderReceiptStatusIcon[status];

  return (
    <HStack
      alignItems="center"
      px="14px"
      py="10px"
      borderRadius="8px"
      bgColor={bgColor}
      width="fit-content"
    >
      <Center paddingRight="5px">
        <Icon icon={icon} color={color} />
      </Center>
      <Text
        color={color}
        fontSize="sm"
        fontWeight="semibold"
        textAlign="center"
      >
        {commonUtils.toTitleCase(status)}
      </Text>
    </HStack>
  );
};

export type LenderStatus =
  | LenderReceiptStatus
  | 'readyForReview'
  | 'inProgress'
  | 'overdue';

export const calculateLenderContainerStatus = (
  container: DeliverableContainerOutputLenderViewGQLType,
): LenderStatus => {
  // if latest receipt exists
  if (container.mostRecentActionType) {
    switch (container.mostRecentActionType) {
      case 'submitted':
        return 'readyForReview';
      case 'approved':
        return 'approved';
      case 'requestRemediation':
        return 'remediationRequested';
      default:
        return finleyAssertUnreachable(container.mostRecentActionType);
    }
  }

  // if receipt doesn't exist, must be either overdue or inProgress
  const currentDate = commonDate.currentDateStr();
  const diff = commonDate.diffDays(container.dueDate, currentDate);
  if (diff < 0) {
    return 'overdue';
  }

  return 'inProgress';
};

export const lenderContainerStatusColor: { [key in LenderStatus]: string } = {
  readyForReview: 'orange',
  approved: 'green',
  remediationRequested: 'orange',
  inProgress: 'orange',
  overdue: 'red',
};

export const lenderContainerStatusIcon: { [key in LenderStatus]: string } = {
  readyForReview: 'mailbox-flag-up',
  approved: 'circle-check',
  remediationRequested: 'messages-question',
  inProgress: 'spinner-third',
  overdue: 'calendar-clock',
};

export const LenderStatusCell = ({
  status,
  ...textProps
}: { status: LenderStatus } & TextProps) => {
  const iconColor = `${lenderContainerStatusColor[status]}.300`;
  const icon = lenderContainerStatusIcon[status];

  return (
    <HStack alignItems="center">
      <Center paddingRight="5px">
        <Icon icon={icon} color={iconColor} />
      </Center>
      <Text color="inherit" fontSize="sm" fontWeight="semibold" {...textProps}>
        {commonUtils.toTitleCase(status)}
      </Text>
    </HStack>
  );
};
